import { createMemo, createSignal, For, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { createRoute } from "@tanstack/solid-router";
import { ArchiveRestore, Bookmark, FileText, Highlighter, Trash2 } from "@fleet/icons";
import {
  appConfigQueryKeys,
  type components,
  queryKeys,
  restoreBookmarkMutation,
  restoreHighlightMutation,
  restoreNoteMutation,
} from "@bso/shared";
import {
  and,
  bible_bookmark,
  bible_highlight,
  bible_highlight_tag,
  bible_note,
  bible_note_tag,
  bible_note_verse,
  bible_verse_highlight,
  eq,
  inArray,
  isNotNull,
} from "@bso/powersync";
import { Card, CardContent, PageContainer, PageHeader } from "@fleet/ui";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { EmptyState } from "@/components/ui/empty-state";
import { appRoute } from "./route";
import { useI18n } from "@/providers/i18n-provider";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useDrizzle } from "@/hooks/use-drizzle";
import { useSession } from "@/lib/auth";
import { api, createApiQuery } from "@/lib/api";
import { createPowerSyncQuery } from "@bso/shared/solid";
import { formatVerseReference } from "@/utils/verse-reference";
import {
  groupTrashedHighlightRows,
  mergeTrashEntries,
  type TrashedBookmarkEntry,
  type TrashedHighlightEntry,
  type TrashedNoteEntry,
  type TrashEntry,
} from "@/features/trash/logic";

/**
 * Trash (UNDO-TRASH-LIGHT L2): notes, highlights, and bookmarks with
 * soft-delete (`deleted_at`).
 *
 * - Deleting from /notes, /highlights, /bookmarks or reader = move here;
 *   children (verses/tags) are PRESERVED (restore brings back entity
 *   intacta).
 * - Restore clears `deleted_at` (views already filter `deleted_at IS
 *   NULL`, reappearing automatically). Highlights restore FULL group
 *   (flat rows + origin group), exact inverse of deletion.
 * - Delete permanently executes real DELETE (entity + children) after
 *   explicit confirmation — irreversible, WITHOUT undo.
 *
 * CONFIG_OVER_CODE: if platform configures retention
 * (`retention.trash_purge_days`), screen ANNOUNCES it. Server silently purging
 * trash after N days cannot hide only in admin panels:
 * data owner needs to know remaining time.
 * With retention off — DEFAULT — nothing is displayed,
 * as there is no deadline to announce.
 */
type PublicConfig = components["schemas"]["PublicConfigResponse"];
type ConfigErrorModel = components["schemas"]["ErrorModel"];

function TrashPage() {
  const { t, locale } = useI18n();
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const session = useSession();

  const [confirmPurge, setConfirmPurge] = createSignal<TrashEntry | null>(null);

  // Offline (normal case here) no data and no deadline announced:
  // better to say nothing than state outdated number
  // vigente.
  const configQuery = createApiQuery<PublicConfig, ConfigErrorModel>(
    () => appConfigQueryKeys.public(),
    async () => api.GET("/api/config", {}),
    () => ({ staleTime: 60_000 }),
  );
  const retentionDays = () => configQuery.data?.trashPurgeDays ?? 0;

  /**
   * ⚠️ Interpolation with `${}`, NOT with `{{}}`.
   *
   * Syntax `msg`… {{date}} …`` (i18next style) DOES NOT WORK: ICU parses
   * `{date}` as argument and outer braces remain on screen
   * («… {30} days …»). Caught by e2e tests. Correct format
   * across repo is `${variable}`, converted into true placeholder.
   * Cleaned up and guarded.
   * `src/__tests__/i18n-placeholder-syntax.test.ts` impide que vuelvan.
   */
  const retentionNotice = () => {
    const days = retentionDays();
    return t(msg`Items here are permanently removed ${days} days after you delete them.`);
  };

  const trashedNotes = createPowerSyncQuery({
    queryKey: () => queryKeys.notes.trashed(session.userId()),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      const uid = session.userId();
      if (!dbInstance || !uid) {
        return null;
      }
      return dbInstance
        .select({
          id: bible_note.id,
          textContent: bible_note.text_content,
          deletedAt: bible_note.deleted_at,
        })
        .from(bible_note)
        .where(and(eq(bible_note.user_id, uid), isNotNull(bible_note.deleted_at)));
    },
    enabled: () => Boolean(db()) && Boolean(drizzle()) && Boolean(session.userId()),
    suspense: false,
  });

  // Deleted FLAT highlight rows; grouping (same criteria as
  // /highlights) is pure logic tested in features/trash/logic.
  const trashedHighlightRows = createPowerSyncQuery({
    queryKey: () => queryKeys.highlights.trashed(session.userId()),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      const uid = session.userId();
      if (!dbInstance || !uid) {
        return null;
      }
      return dbInstance
        .select({
          highlightId: bible_verse_highlight.id,
          originId: bible_verse_highlight.origin_id,
          color: bible_verse_highlight.color,
          deletedAt: bible_verse_highlight.deleted_at,
          version: bible_verse_highlight.version,
          book: bible_verse_highlight.book,
          chapter: bible_verse_highlight.chapter,
          verse: bible_verse_highlight.verse,
        })
        .from(bible_verse_highlight)
        .where(
          and(eq(bible_verse_highlight.user_id, uid), isNotNull(bible_verse_highlight.deleted_at)),
        );
    },
    enabled: () => Boolean(db()) && Boolean(drizzle()) && Boolean(session.userId()),
    suspense: false,
  });

  const trashedBookmarks = createPowerSyncQuery({
    queryKey: () => queryKeys.bookmarks.trashed(session.userId()),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      const uid = session.userId();
      if (!dbInstance || !uid) {
        return null;
      }
      return dbInstance
        .select({
          id: bible_bookmark.id,
          deletedAt: bible_bookmark.deleted_at,
          version: bible_bookmark.version,
          book: bible_bookmark.book,
          chapter: bible_bookmark.chapter,
          verse: bible_bookmark.verse,
        })
        .from(bible_bookmark)
        .where(and(eq(bible_bookmark.user_id, uid), isNotNull(bible_bookmark.deleted_at)));
    },
    enabled: () => Boolean(db()) && Boolean(drizzle()) && Boolean(session.userId()),
    suspense: false,
  });

  const entries = createMemo<TrashEntry[]>(() => {
    const notes: TrashedNoteEntry[] = (trashedNotes.data ?? []).map((n) => ({
      kind: "note",
      id: n.id,
      textContent: n.textContent,
      deletedAt: n.deletedAt,
    }));
    const highlights = groupTrashedHighlightRows(trashedHighlightRows.data ?? []);
    const bookmarks: TrashedBookmarkEntry[] = [];
    for (const b of trashedBookmarks.data ?? []) {
      if (b.book === null || b.chapter === null || b.verse === null) {
        continue;
      }
      bookmarks.push({
        kind: "bookmark",
        id: b.id,
        deletedAt: b.deletedAt,
        version: b.version ?? "kjv",
        book: b.book,
        chapter: b.chapter,
        verse: b.verse,
      });
    }
    return mergeTrashEntries(notes, highlights, bookmarks);
  });

  const deletedLabel = (iso: string | null) => {
    if (!iso) {
      return "";
    }
    const date = new Date(iso);
    return Number.isNaN(date.getTime())
      ? ""
      : new Intl.DateTimeFormat(locale(), { dateStyle: "medium", timeStyle: "short" }).format(date);
  };

  const restoreNote = async (id: string) => {
    const dbInstance = drizzle();
    if (!dbInstance) {
      return;
    }
    await restoreNoteMutation(dbInstance, id);
  };

  // Exact inverse of /highlights soft delete: flat rows + origin group.
  const restoreHighlight = async (entry: TrashedHighlightEntry) => {
    const dbInstance = drizzle();
    if (!dbInstance) {
      return;
    }
    await restoreHighlightMutation(dbInstance, entry.rowIds, entry.originId);
  };

  const restoreBookmark = async (id: string) => {
    const dbInstance = drizzle();
    if (!dbInstance) {
      return;
    }
    await restoreBookmarkMutation(dbInstance, id);
  };

  const restoreEntry = async (entry: TrashEntry) => {
    if (entry.kind === "note") {
      await restoreNote(entry.id);
    } else if (entry.kind === "highlight") {
      await restoreHighlight(entry);
    } else {
      await restoreBookmark(entry.id);
    }
  };

  const purgeEntry = async (entry: TrashEntry) => {
    const dbInstance = drizzle();
    if (!dbInstance) {
      return;
    }
    if (entry.kind === "note") {
      await dbInstance.delete(bible_note_tag).where(eq(bible_note_tag.note_id, entry.id));
      await dbInstance.delete(bible_note_verse).where(eq(bible_note_verse.note_id, entry.id));
      await dbInstance.delete(bible_note).where(eq(bible_note.id, entry.id));
    } else if (entry.kind === "highlight") {
      await dbInstance
        .delete(bible_verse_highlight)
        .where(inArray(bible_verse_highlight.id, entry.rowIds));
      if (entry.originId) {
        await dbInstance
          .delete(bible_highlight_tag)
          .where(eq(bible_highlight_tag.highlight_id, entry.originId));
        await dbInstance.delete(bible_highlight).where(eq(bible_highlight.id, entry.originId));
      }
    } else {
      await dbInstance.delete(bible_bookmark).where(eq(bible_bookmark.id, entry.id));
    }
  };

  const previewText = (text: string) => {
    const clean = text.trim().replaceAll(/\s+/g, " ");
    return clean.length > 0 ? clean : t(msg`(empty note)`);
  };

  const entryTitle = (entry: TrashEntry) => {
    if (entry.kind === "note") {
      return previewText(entry.textContent ?? "");
    }
    if (entry.kind === "highlight") {
      return formatVerseReference(entry.verses, t(msg`Highlight`));
    }
    return formatVerseReference([entry], t(msg`Bookmark`));
  };

  const kindLabel = (entry: TrashEntry) => {
    if (entry.kind === "note") {
      return t(msg`Note`);
    }
    if (entry.kind === "highlight") {
      return t(msg`Highlight`);
    }
    return t(msg`Bookmark`);
  };

  const purgeDescription = (entry: TrashEntry | null) => {
    if (entry?.kind === "highlight") {
      return t(msg`This permanently deletes the highlight and its tags. This cannot be undone.`);
    }
    if (entry?.kind === "bookmark") {
      return t(msg`This permanently deletes the bookmark. This cannot be undone.`);
    }
    return t(msg`This permanently deletes the note, its verses and tags. This cannot be undone.`);
  };

  return (
    <PageContainer maxWidth="2xl" class="space-y-6" containerClass="scrollbar-thin">
      <div data-testid="trash-view">
        <PageHeader
          title={t(msg`Trash`)}
          description={t(
            msg`Deleted notes, highlights and bookmarks land here and can be restored anytime.`,
          )}
          backTo="/profile"
          icon={Trash2}
        />
      </div>

      <Show when={retentionDays() > 0}>
        <p class="text-xs text-muted-foreground" data-testid="trash-retention-notice">
          {retentionNotice()}
        </p>
      </Show>

      <Show
        when={entries().length > 0}
        fallback={
          <div data-testid="trash-empty">
            <EmptyState
              icon={Trash2}
              title={t(msg`Trash is empty`)}
              description={t(
                msg`Notes, highlights and bookmarks you delete land here — nothing is lost right away.`,
              )}
            />
          </div>
        }
      >
        <div class="flex flex-col gap-2">
          <For each={entries()}>
            {(entry) => (
              <Card
                data-testid="trash-row"
                data-kind={entry.kind}
                data-note-text={entry.kind === "note" ? (entry.textContent ?? "") : undefined}
                data-ref={entry.kind === "note" ? undefined : entryTitle(entry)}
              >
                <CardContent class="flex items-center gap-3 p-3">
                  <span class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary opacity-80">
                    <Show when={entry.kind === "note"}>
                      <FileText class="h-5 w-5" />
                    </Show>
                    <Show when={entry.kind === "highlight"}>
                      <Highlighter class="h-5 w-5" />
                    </Show>
                    <Show when={entry.kind === "bookmark"}>
                      <Bookmark class="h-5 w-5" />
                    </Show>
                  </span>
                  <div class="min-w-0 flex-1">
                    <p class="flex items-center gap-2 truncate text-sm font-medium">
                      <Show when={entry.kind === "highlight" ? entry : null}>
                        {(h) => (
                          <span
                            aria-hidden="true"
                            class="inline-block h-2.5 w-2.5 shrink-0 rounded-full"
                            style={{ "background-color": h().color }}
                          />
                        )}
                      </Show>
                      <span class="truncate">{entryTitle(entry)}</span>
                    </p>
                    <p class="mt-0.5 text-xs text-muted-foreground">
                      {kindLabel(entry)}
                      {" · "}
                      {t(msg`Deleted {date}`, { date: deletedLabel(entry.deletedAt) })}
                    </p>
                  </div>
                  <div class="flex shrink-0 items-center gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      data-testid="trash-restore"
                      disabled={!drizzle()}
                      onClick={() => void restoreEntry(entry)}
                    >
                      <ArchiveRestore class="mr-1 h-4 w-4" />
                      {t(msg`Restore`)}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      class="text-destructive hover:text-destructive"
                      data-testid="trash-delete-forever"
                      disabled={!drizzle()}
                      onClick={() => setConfirmPurge(entry)}
                    >
                      <Trash2 class="mr-1 h-4 w-4" />
                      {t(msg`Delete forever`)}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </For>
        </div>
      </Show>

      <ConfirmDialog
        open={confirmPurge() !== null}
        onOpenChange={(open) => {
          if (!open) {
            setConfirmPurge(null);
          }
        }}
        title={t(msg`Delete forever?`)}
        description={purgeDescription(confirmPurge())}
        confirmLabel={t(msg`Delete forever`)}
        variant="destructive"
        onConfirm={() => {
          const entry = confirmPurge();
          setConfirmPurge(null);
          if (entry) {
            void purgeEntry(entry);
          }
        }}
      />
    </PageContainer>
  );
}

export const profileTrashRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/profile/trash",
  component: TrashPage,
});
